/**
 * The passenger route sheet's create form — the other half of `DecaCreateScreen`.
 *
 * Four `FormSectionCard`s in the order of `PASSENGER_SECTIONS`, mirroring the
 * sheet's own sections (design D3): carrier, hirer, services, bus and driver.
 * Section 3 is the repeatable one — "Agrupación de servicios" turns it into a
 * list (BOE Sixth) while both parties stay on the document, exactly as grouping
 * works on the goods form.
 *
 * 🚨 **The rule that is inverted here, and must stay inverted.** On the goods
 * DeCA `nif_conductor` is mandatory to capture and **never printed**
 * (`DICCIONARIO_CAMPOS` 17) — and our own goods screen says so on the field. On
 * the route sheet `conductor_nombre` and `conductor_nif` **are printed**: Art.
 * 2(e) of Orden FOM/1230/2013 makes both content of the sheet, and the client
 * instructed it in writing on 2026-09-09 after our contrary recommendation and
 * its reason were put to them (Addendum No. 2, Clause 4.2 — a documented
 * instruction of the Controller). Section 4 therefore carries
 * `passengerCreate.driverOnPdf`, which states it out loud: we trained this user
 * on the opposite promise one screen away, so silence here would be a trap of
 * our own making. Do not "fix" this to match goods, and do not relax the goods
 * rule to match this.
 *
 * **Where this deliberately departs from `GoodsCreateForm`:**
 *
 *  - **No liable parties, so no party rail and no penalty.** FOM/1230/2013 has
 *    no Cargador/Transportista split; the carrier undertaking answers for the
 *    sheet as a whole (client answer 1, 2026-09-09). The €401-per-party notice
 *    has no passenger analogue. The rail here is one count, not four rows.
 *  - **The class drives the rules, not just the labels.** `servicio_clase`
 *    makes `servicio_paradas` mandatory for `DISCRECIONAL` and `TURISTICO`
 *    (Art. 2(c)) and `servicio_clase_ref` mandatory for `REFUERZO` and
 *    `COLABORACION` (Art. 2(f)), per service row — which is why `FormField`
 *    grew a `mandatory` override: the requirement is per *row*, and a
 *    dictionary lookup takes a column.
 *  - **Per-service driver and bus, on a grouped sheet only.** BOE Sixth refuses
 *    goods grouping outright when the tractor, trailer or driver differ; its
 *    passenger paragraph *permits* exactly that and requires them to be
 *    identified per service. So the same three columns are mandatory on the
 *    sheet and optional inside a **numbered** service, where blank means "the
 *    sheet's own". A single-service sheet is not offered them and does not send
 *    them: it is the sheet, so it has nothing to depart from, and one document
 *    naming two drivers is a contradiction a roadside inspector reads off the
 *    PDF. See `OVERRIDE_COLUMNS`, `ServiceBlock` and `toInput`.
 *  - **The 422 is flat.** A passenger validation failure carries `fieldErrors`,
 *    not `partyErrors`, so `validationProblemOf` answers `null` for one — see
 *    `passengerValidationProblemOf`. The per-party summary that goods renders
 *    from the same 422 has nothing to render here, so the rail is
 *    `PassengerValidationSummary`: one flat list, no party headings and no
 *    €401-per-party sentence.
 *
 * Neither conditional field is hidden when its class is not selected. The
 * contract permits a value either way (`servicio_paradas` and
 * `servicio_clase_ref` are both nullable, with no rule forbidding one), so
 * hiding them would stop an operator recording stops on a `REFUERZO` service
 * that makes them. What the class changes is whether the field is required.
 */

import { useMemo, useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { FormProvider, useFieldArray, useForm, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
  ifMatch,
  unwrap,
  unwrapWithETag,
  type FieldError as ApiFieldError,
  type PassengerDeca,
  type PassengerDecaInput,
  type PassengerFieldError,
} from '@deca/api'

import { api } from '../app/api.ts'
import {
  apiErrorMessage,
  isConflict,
  isExternalRefTaken,
  passengerErrorsByField,
  passengerValidationProblemOf,
} from '../app/errors.ts'
import { useAuth } from '../app/auth.tsx'
import { useInvalidatePassengerDeca } from '../app/queries.ts'
import { downloadWindowDays, isGenerated } from '../deca/estado.ts'
import { joinServiceDate, localInputToRfc3339, SERVICE_DATE_BOUNDS, splitServiceDate } from '../deca/format.ts'
import {
  isMandatoryForClass,
  passengerFieldDef,
  passengerFieldsForSection,
  PASSENGER_FIELDS,
  PASSENGER_SECTION_NUMBER,
  SERVICE_CLASSES,
  type ServiceClassValue,
} from '../deca/passenger/fields.ts'
import { EstadoChip } from '../ui/EstadoChip.tsx'
import {
  DecaFormProvider,
  FormField,
  FormSectionCard,
  MissingFieldsButton,
  SelectInput,
  TextInput,
} from '../ui/form.tsx'
import { PassengerValidationSummary } from '../ui/PassengerValidationSummary.tsx'
import { Button, Card, Datum, IconButton, Notice } from '../ui/primitives.tsx'
import { partyField, type PartyColumns } from '../ui/partyFill.tsx'
import { SaveToBookAction } from '../ui/SaveToBookAction.tsx'
import { SoyYoAction } from '../ui/SoyYoAction.tsx'
import { useToast } from '../ui/Toast.tsx'
import type { BookField } from '../ui/bookField.ts'

/**
 * One service row. The last three are the BOE Sixth overrides: empty means the
 * service is performed by the sheet's own driver and bus, and `toInput` turns
 * that empty string into the `null` the contract asks for.
 */
export interface ServiceValues {
  servicio_origen: string
  servicio_destino: string
  /** The Madrid day, `YYYY-MM-DD` — the date control. */
  servicio_fecha: string
  /**
   * The hour beside it, `HH:MM`, or empty for a service with no set time (#102). Its own control,
   * because `datetime-local` cannot be left half-empty and an empty hour is what has to be said.
   */
  servicio_hora: string
  servicio_clase: ServiceClassValue | ''
  servicio_paradas: string
  servicio_clase_ref: string
  conductor_nombre: string
  conductor_nif: string
  matricula_autobus: string
}

/** Exported alongside `toInput`, which cannot be called from a test without it. */
export interface PassengerFormValues {
  externalRef: string
  transportista_nombre: string
  transportista_nif: string
  contratante_nombre: string
  contratante_nif: string
  matricula_autobus: string
  conductor_nombre: string
  conductor_nif: string
  services: ServiceValues[]
}

const EMPTY_SERVICE: ServiceValues = {
  servicio_origen: '',
  servicio_destino: '',
  servicio_fecha: '',
  servicio_hora: '',
  servicio_clase: '',
  servicio_paradas: '',
  servicio_clase_ref: '',
  conductor_nombre: '',
  conductor_nif: '',
  matricula_autobus: '',
}

const DEFAULTS: PassengerFormValues = {
  externalRef: '',
  transportista_nombre: '',
  transportista_nif: '',
  contratante_nombre: '',
  contratante_nif: '',
  matricula_autobus: '',
  conductor_nombre: '',
  conductor_nif: '',
  services: [{ ...EMPTY_SERVICE }],
}

/**
 * The three columns BOE Sixth lets one service of a **grouped** sheet override —
 * the sheet's own driver and bus, restated for a service that departs from them.
 *
 * They are on screen twice only while the sheet is grouped: once in section 4 as
 * the sheet's own value, once inside each service block. `ServiceBlock` renders
 * this block for a numbered service only, and `toInput` drops the values when
 * the sheet is not grouped — so a single-service sheet names exactly one driver
 * and one bus.
 */
const OVERRIDE_COLUMNS: readonly string[] = [
  'conductor_nombre',
  'conductor_nif',
  'matricula_autobus',
]

/**
 * The transport fields the address book fills (#221): the bus's plate and the driver's name and
 * DNI/NIE, on the sheet or, under [prefix], as one service's own. A driver chosen from either field
 * fills both, and is saved from beside the DNI/NIE with the name typed beside it.
 */
function transportBook(column: string, prefix = ''): BookField | undefined {
  const driver = { name: `${prefix}conductor_nombre`, nif: `${prefix}conductor_nif` }
  switch (column) {
    case 'matricula_autobus':
      return { kind: 'VEHICLE', column: `${prefix}matricula_autobus` }
    case 'conductor_nombre':
      return { kind: 'DRIVER', columns: driver, field: 'name' }
    case 'conductor_nif':
      return { kind: 'DRIVER', columns: driver, field: 'nif' }
    default:
      return undefined
  }
}

/** The maximum the contract accepts — `services` is `maxItems: 50`. */
const MAX_SERVICES = 50

/**
 * The sheet's two parties' columns: what «Soy yo» fills on the carrier, and what the address book
 * searches, fills and saves on both (#159).
 *
 * Two columns each, not three, and `_nombre` rather than `_razon_social`: the route sheet's party
 * fields come from Art. 2 of Orden FOM/1230/2013, which carries no address for either party. Named
 * explicitly for that reason — see `PartyColumns`.
 */
const PARTY_COLUMNS = {
  transportista: { name: 'transportista_nombre', nif: 'transportista_nif' },
  contratante: { name: 'contratante_nombre', nif: 'contratante_nif' },
} as const satisfies Record<'transportista' | 'contratante', PartyColumns>

/**
 * The form's values as the contract wants them.
 *
 * `grouped` is a parameter rather than a closed-over binding for the reason the
 * goods twin states: the `slice(0, 1)` on the next line is the only thing
 * standing between a service the user toggled away and a generated PDF. Leaving
 * grouping does not remove the appended row — it vanishes from the screen and
 * stays in react-hook-form's state, where `shouldUnregister` defaults to false —
 * and a payload carrying it does not look broken from the server's side. It is
 * a complete service with its own origin, destination, date and class, and it
 * would be printed onto a route sheet a driver hands to an inspector,
 * describing a journey nobody is making.
 *
 * ⚠️ `servicio_fecha` goes through `localInputToRfc3339`, exactly as the goods
 * `fecha_realizacion` does. The contract types it `format: date-time` and
 * rejects a value with no offset rather than guessing one. Since #102 the hour
 * is optional: a blank one sends the day's 00:00 in Madrid with
 * `servicioFechaHasTime: false`, which is what makes BOE First.2 judge the
 * service by its day — midnight is also a real departure time, so the instant
 * alone could not say which it is.
 */
export function toInput(raw: PassengerFormValues, grouped: boolean): PassengerDecaInput {
  const rows = grouped ? raw.services : raw.services.slice(0, 1)

  // The three BOE Sixth overrides survive a toggle exactly as a whole service
  // row does, one level deeper: `slice(0, 1)` keeps row 0, so an override typed
  // into row 0 under grouping rides along inside the row that is kept.
  // `ServiceBlock` stops rendering these inputs once the sheet is not grouped,
  // but react-hook-form keeps what was typed (`shouldUnregister` defaults to
  // false), and the consequence is worse than a phantom service: the sheet
  // would name one driver in section 4 and its only service another, and
  // `backend/src/pdf/render-passenger.ts` prints both — one legal document
  // contradicting itself, with nothing on screen to say so. A single service
  // *is* the sheet; there is nothing for it to depart from, so the three
  // columns are dropped rather than sent.
  const serviceOverride = (value: string) => (grouped ? value || null : null)

  return {
    ...(raw.externalRef ? { externalRef: raw.externalRef } : {}),
    transportista_nombre: raw.transportista_nombre,
    transportista_nif: raw.transportista_nif,
    contratante_nombre: raw.contratante_nombre,
    contratante_nif: raw.contratante_nif,
    matricula_autobus: raw.matricula_autobus,
    conductor_nombre: raw.conductor_nombre,
    conductor_nif: raw.conductor_nif,
    services: rows.map((row) => ({
      servicio_origen: row.servicio_origen,
      servicio_destino: row.servicio_destino,
      servicio_fecha: localInputToRfc3339(joinServiceDate(row.servicio_fecha, row.servicio_hora)),
      servicioFechaHasTime: row.servicio_hora !== '',
      servicio_clase: row.servicio_clase as ServiceClassValue,
      // Empty means absent, and the contract distinguishes the two: `''` would
      // print an empty «Paradas intermedias» onto a sheet where the field is
      // meant not to appear, and an empty override would claim this service has
      // a driver of its own who has no name.
      servicio_paradas: row.servicio_paradas || null,
      servicio_clase_ref: row.servicio_clase_ref || null,
      conductor_nombre: serviceOverride(row.conductor_nombre),
      conductor_nif: serviceOverride(row.conductor_nif),
      matricula_autobus: serviceOverride(row.matricula_autobus),
    })),
  }
}

/**
 * The wire shape back into the form — the inverse of `toInput`, and the half of #90 that lets a
 * draft be reopened in the form that created it.
 *
 * Every conversion here answers one `toInput` made going out. The service date goes back through
 * `splitServiceDate`, Madrid-pinned by `madridParts` rather than reading the host clock, so a draft
 * shows the wall-clock time it was typed in whatever the browser's zone is — and a service with no
 * hour opens on an empty hour box rather than a `00:00` nobody typed (#102): the flag decides,
 * never the hour.
 * Everything nullable comes back as `''`: React logs a controlled-input warning when a controlled
 * value turns null, and the box stops accepting typing.
 *
 * 🚨 **`servicio_clase` is the one the contract lies about.** It is typed required and
 * non-nullable, and a draft is exactly where that is untrue — a sheet saved before anyone picked
 * a class is stored with none, because the column is a `pgEnum` and `''` is not a member of it.
 * The `??` on that line is load-bearing against the very value the type says cannot arrive.
 *
 * The three per-service overrides are mapped like any other field. `toInput` decides whether they
 * are sent, from `grouped`, and it has to stay the only place that decides: a mapper that dropped
 * them on the way in would silently clear a grouped sheet's per-service driver on the first save.
 */
export function fromPassengerDeca(sheet: PassengerDeca): PassengerFormValues {
  const rows: ServiceValues[] = (sheet.services ?? []).map((row) => ({
    servicio_origen: row.servicio_origen ?? '',
    servicio_destino: row.servicio_destino ?? '',
    ...(({ date, time }) => ({ servicio_fecha: date, servicio_hora: time }))(
      splitServiceDate(row.servicio_fecha, row.servicioFechaHasTime),
    ),
    servicio_clase: row.servicio_clase ?? '',
    servicio_paradas: row.servicio_paradas ?? '',
    servicio_clase_ref: row.servicio_clase_ref ?? '',
    conductor_nombre: row.conductor_nombre ?? '',
    conductor_nif: row.conductor_nif ?? '',
    matricula_autobus: row.matricula_autobus ?? '',
  }))

  return {
    externalRef: sheet.externalRef ?? '',
    transportista_nombre: sheet.transportista_nombre ?? '',
    transportista_nif: sheet.transportista_nif ?? '',
    contratante_nombre: sheet.contratante_nombre ?? '',
    contratante_nif: sheet.contratante_nif ?? '',
    matricula_autobus: sheet.matricula_autobus ?? '',
    conductor_nombre: sheet.conductor_nombre ?? '',
    conductor_nif: sheet.conductor_nif ?? '',
    // One empty service rather than none: `ServiceBlock` renders index 0 unconditionally, so a
    // draft saved with no services would leave the whole of section 3 unrendered.
    services: rows.length ? rows : [{ ...EMPTY_SERVICE }],
  }
}

/**
 * A copy of an existing route sheet, for «Duplicar» — #178. `fromPassengerDeca`'s values with every
 * service's date cleared, since each must be chosen again (the hours are kept, Aleksey 2026-10-06),
 * and `externalRef`, for the reason `copyOfDeca` gives: it names one transport in the company's own
 * systems, and a copy is a new one. The per-service overrides travel with their services, as on a
 * reopened draft: `toInput` decides whether they are sent, from `grouped`.
 */
export function copyOfPassengerDeca(source: PassengerDeca): PassengerFormValues {
  const values = fromPassengerDeca(source)
  return {
    ...values,
    externalRef: '',
    services: values.services.map((row) => ({ ...row, servicio_fecha: '' })),
  }
}

/**
 * How many mandatory fields are still empty — a live hint, never the gate.
 *
 * The authoritative answer is the server's 422. This exists for the same reason
 * the goods rail's `partyProgress` does, minus the parties: it decides whether
 * the primary button is offered, so that a user is not invited to generate a
 * document that cannot be generated. Class-aware through `isMandatoryForClass`,
 * so choosing `REFUERZO` adds the field Art. 2(f) then requires.
 */
export function missingMandatoryCount(values: PassengerFormValues, grouped: boolean): number {
  const filled = (value: unknown) => typeof value === 'string' && value.trim() !== ''
  let missing = 0

  for (const field of PASSENGER_FIELDS) {
    if (field.scope !== 'document' || !field.mandatory) continue
    if (!filled(values[field.column as keyof PassengerFormValues])) missing += 1
  }

  const rows = grouped ? values.services : values.services.slice(0, 1)
  for (const row of rows) {
    for (const field of PASSENGER_FIELDS) {
      if (field.scope !== 'service') continue
      if (!isMandatoryForClass(field, row.servicio_clase)) continue
      if (!filled(row[field.column as keyof ServiceValues])) missing += 1
    }
  }
  return missing
}

/**
 * The form's three modes: creating, editing a draft (#90) and copying a sheet (#178). A union, so no
 * caller can pass `draft` and `source` together: the values and the submit would follow the draft,
 * a `PATCH`, while the banner named a copy.
 */
type PassengerCreateFormProps =
  | { draft: PassengerDeca; etag?: string; source?: never }
  | { source: PassengerDeca; draft?: never; etag?: never }
  | { draft?: undefined; etag?: undefined; source?: undefined }

/**
 * `draft` and `etag` together turn this into the **edit** form — #90. Absent, it creates; present,
 * every save is a `PATCH` against that sheet. They travel as a pair because neither is any use
 * alone: `PATCH`, `generate`, `modifications` and `supersede` all declare `If-Match` **required**,
 * and `GET /passenger-decas/{sheetId}` is the only place an `ETag` is handed out.
 *
 * `source` is the third mode — #178, «Duplicar»: the form is filled from an existing sheet by
 * `copyOfPassengerDeca` and still creates, so the copy gets its own number, QR and CSV and the
 * source is never written.
 */
export function PassengerCreateForm({ draft, etag, source }: PassengerCreateFormProps = {}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const toast = useToast()
  const invalidate = useInvalidatePassengerDeca()
  const { principal } = useAuth()

  const form = useForm<PassengerFormValues>({
    defaultValues: draft ? fromPassengerDeca(draft) : source ? copyOfPassengerDeca(source) : DEFAULTS,
    mode: 'onBlur',
  })
  const services = useFieldArray({ control: form.control, name: 'services' })

  // 🚨 Seeded from the draft, not `false`. `toInput` slices to the first service when this is off
  // and `PassengerDecaPatch.services` REPLACES the whole array, so a grouped draft reopening
  // ungrouped would delete its other journeys on the first save. Derived from the array that
  // would be lost rather than from the server's `isGrouped`, because the two cannot then disagree
  // about the very data this protects.
  // A copy is seeded the same way (#178): a grouped source opens grouped, with every service.
  const [grouped, setGrouped] = useState(((draft ?? source)?.services?.length ?? 0) > 1)
  const [serverErrors, setServerErrors] = useState<Record<string, ApiFieldError>>({})
  // The same failures twice, on purpose: keyed by field for the inputs, and as the
  // server's own ordered list for the rail. `passengerErrorsByField` keeps only the
  // last error per field, so the map alone cannot be what the summary reads.
  const [fieldErrors, setFieldErrors] = useState<PassengerFieldError[] | undefined>()
  const [submitError, setSubmitError] = useState<string | null>(null)
  /**
   * The reference the server refused as another route sheet's — #92. A mark drawn from state, for
   * the reasons `GoodsCreateForm` gives: held as react-hook-form state it either went with the next
   * blur or, as a `validate` rule, stopped *Generar* from ever asking the server again.
   */
  const [refusedRef, setRefusedRef] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  /**
   * The sheet's version as this form last wrote it: the `ETag` it was mounted with, then the one
   * each `PATCH` returns — #132, the goods form's rule. A refused *Generar* keeps the form open
   * after its `PATCH` has moved the version, so the next submit has to carry that `PATCH`'s `ETag`.
   * The values on screen are exactly the ones that `PATCH` stored, and a real conflict still
   * answers 412.
   */
  const [draftEtag, setDraftEtag] = useState<string | null | undefined>(etag)

  const values = form.watch()
  const externalRefMark =
    refusedRef !== null && values.externalRef === refusedRef ? t('errors.code.EXTERNAL_REF_TAKEN') : undefined
  const missing = useMemo(() => missingMandatoryCount(values, grouped), [values, grouped])
  const complete = missing === 0
  /** What the rail is listing, for the phone's count button: the server's list once it has answered. */
  const missingCount = fieldErrors
    ? fieldErrors.filter((error) => error.severity !== 'WARNING').length
    : missing
  /** The rail's validation card, where that button takes the user — see `MissingFieldsButton`. */
  const validationCard = useRef<HTMLDivElement>(null)

  async function submit(raw: PassengerFormValues, generate: boolean) {
    setBusy(true)
    setSubmitError(null)
    setServerErrors({})
    setFieldErrors(undefined)
    setRefusedRef(null)
    try {
      let created: PassengerDeca
      if (draft) {
        // A draft is amended, never re-created: a second `POST` would take another number from
        // the route sheet's own counter and leave the first draft behind. `PATCH` is a genuine
        // partial update whose completeness is only checked at `generate`, which is what lets an
        // incomplete draft be saved again and again.
        const patched = await unwrapWithETag(
          api.PATCH('/api/v1/passenger-decas/{sheetId}', {
            params: { path: { sheetId: draft.id }, header: ifMatch(draftEtag) },
            // 🚨 `externalRef` is sent explicitly here, and only here. `toInput` omits an empty
            // one, which is right on a create — the alternative is storing an explicit null
            // where the caller said nothing — and wrong on a PATCH, where an omitted key means
            // "unchanged". Without this line a user can set an external reference on a draft but
            // never clear it, and the screen would tell them the draft was saved.
            body: { ...toInput(raw, grouped), externalRef: raw.externalRef || null },
          }),
        )
        // Kept before generating, so a refused *Generar* leaves the form on the version it wrote.
        setDraftEtag(patched.etag)
        // 🚨 The amendment moved the version, so generation must carry the `ETag` the `PATCH`
        // *returned*. Re-using the one this form was mounted with is a 412 every time.
        created = generate
          ? await unwrap(
              api.POST('/api/v1/passenger-decas/{sheetId}/generate', {
                params: { path: { sheetId: draft.id }, header: ifMatch(patched.etag) },
              }),
            )
          : patched.data
      } else {
        created = await unwrap(
          api.POST('/api/v1/passenger-decas', { body: { ...toInput(raw, grouped), generate } }),
        )
      }
      invalidate(created.id)

      // Report what came back, not what was asked for — the goods screen's rule,
      // and the same roadside consequence: a sheet still `BORRADOR` has no PDF,
      // no QR and no direct-download URL, so telling the user it was generated
      // sends them to hand a driver a document that does not exist.
      if (generate && !isGenerated(created)) {
        toast.error(t('passengerCreate.notGenerated'))
      } else {
        toast.success(
          generate
            ? t('passengerCreate.generated', { documentNumber: created.documentNumber ?? '' })
            : t('create.savedDraft'),
        )
      }
      void navigate({ to: '/hojas-de-ruta/$sheetId', params: { sheetId: created.id } })
    } catch (error) {
      const problem = passengerValidationProblemOf(error)
      if (problem) {
        setServerErrors(passengerErrorsByField(problem))
        setFieldErrors(problem.fieldErrors)
        setSubmitError(null)
      } else if (isConflict(error)) {
        // The sheet moved under this form. ⚠️ And DELIBERATELY not a silent re-read, though the
        // issue's wording invites one: `useForm` applies `defaultValues` on mount only, so
        // refreshing the draft behind this form would swap in a newer `ETag` while leaving the
        // user's stale values on screen — turning "refuses to overwrite" into "overwrites on the
        // second click". The wording is the route sheet's own, not the DeCA's.
        setSubmitError(t('passengerModify.versionConflict'))
      } else {
        // #92 — refused for its reference: on the field as well as in the notice.
        if (isExternalRefTaken(error)) setRefusedRef(raw.externalRef)
        setSubmitError(apiErrorMessage(error, t))
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <FormProvider {...form}>
      <DecaFormProvider
        value={{
          serverErrors,
          // The passenger dictionary, as one object: a caller able to supply
          // `lookup` without `sectionNumber` could print passenger fields under
          // goods numbering, which type-checks and is wrong on a legal document.
          dictionary: {
            lookup: passengerFieldDef,
            sectionNumber: PASSENGER_SECTION_NUMBER,
            sectionLabelKey: (section) => `passengerSections.${section}`,
          },
        }}
      >
        <form className="stack create-form" onSubmit={(event) => event.preventDefault()}>
          {/* #178 — what this form is a copy of, and what the copy still needs: a date for each
              service. A draft is shown as «Borrador de hoja de ruta» until it is generated, never by
              its number (the detail screen's rule), so a copy of one is named as a draft. */}
          {source ? (
            <Notice tone="info">
              {`${
                isGenerated(source)
                  ? t('duplicate.copyOf', { documentNumber: source.documentNumber ?? '' })
                  : t('duplicate.copyOfDraft')
              } ${t('duplicate.chooseServiceDates')}`}
            </Notice>
          ) : null}

          <div className="segmented" role="group" aria-label={t('passengerCreate.modeLabel')}>
            <button
              type="button"
              className="segmented__option"
              aria-pressed={!grouped}
              onClick={() => {
                setGrouped(false)
                // The click that switched the mode has just blurred the field the user was in, and
                // `mode: 'onBlur'` marked it if it was empty. The switch is not the user leaving that
                // field; the marks return on the next blur or on Generar.
                form.clearErrors()
              }}
            >
              {t('passengerCreate.modeSingle')}
            </button>
            <button
              type="button"
              className="segmented__option"
              aria-pressed={grouped}
              onClick={() => {
                setGrouped(true)
                // A grouping of one is not a grouping. Not focused, though: `append` puts the
                // cursor in the row it adds, which scrolled the page past the carrier and the hirer
                // to the second service (the goods form's «Agrupación de envíos», Paco 2026-09-30).
                if (services.fields.length === 1)
                  services.append({ ...EMPTY_SERVICE }, { shouldFocus: false })
                // Not marked either: see «Un solo servicio» above.
                form.clearErrors()
              }}
            >
              {t('passengerCreate.modeGrouped')}
            </button>
          </div>

          <Notice tone="info">
            {grouped ? t('passengerCreate.groupBanner') : t('passengerCreate.singleBanner')}
          </Notice>

          <div className="form-layout">
            <div className="stack">
              {submitError ? <Notice tone="danger">{submitError}</Notice> : null}

              <FormSectionCard
                section="transportista"
                actions={
                  // The carrier's own two columns — see `PARTY_COLUMNS`.
                  <div className="party-actions">
                    <SoyYoAction columns={PARTY_COLUMNS.transportista} />
                    <SaveToBookAction columns={PARTY_COLUMNS.transportista} />
                  </div>
                }
              >
                <div className="form-grid form-grid--full">
                  {passengerFieldsForSection('transportista').map((field, index) => (
                    <FormField
                      key={field.column}
                      column={field.column}
                      autoFocus={index === 0}
                      addressBook={partyField(PARTY_COLUMNS.transportista, field.column)}
                    />
                  ))}
                </div>
              </FormSectionCard>

              {/* No «Soy yo» on the hirer, so «Guardar en la libreta» stands alone here (#159). */}
              <FormSectionCard
                section="contratante"
                actions={<SaveToBookAction columns={PARTY_COLUMNS.contratante} />}
              >
                <div className="form-grid form-grid--full">
                  {passengerFieldsForSection('contratante').map((field) => (
                    <FormField
                      key={field.column}
                      column={field.column}
                      addressBook={partyField(PARTY_COLUMNS.contratante, field.column)}
                    />
                  ))}
                </div>
              </FormSectionCard>

              <FormSectionCard
                section="servicios"
                actions={
                  grouped ? (
                    <span className="chip chip--info">{services.fields.length}</span>
                  ) : null
                }
              >
                <div className="stack">
                  {(grouped ? services.fields : services.fields.slice(0, 1)).map((row, index) => (
                    <ServiceBlock
                      key={row.id}
                      index={index}
                      seq={grouped ? index + 1 : undefined}
                      serverErrors={serverErrors}
                      onRemove={
                        grouped && services.fields.length > 1
                          ? () => services.remove(index)
                          : undefined
                      }
                    />
                  ))}

                  {grouped ? (
                    <div>
                      <Button
                        icon="plus"
                        onClick={() => services.append({ ...EMPTY_SERVICE })}
                        disabled={services.fields.length >= MAX_SERVICES}
                      >
                        {t('passengerCreate.addService')}
                      </Button>
                    </div>
                  ) : null}
                </div>
              </FormSectionCard>

              <FormSectionCard section="transporte">
                <div className="stack">
                  {/* 🚨 Art. 2(e). Our own goods screen promises the opposite
                      about `nif_conductor`, so this has to be said rather than
                      merely not contradicted. */}
                  <Notice tone="info">{t('passengerCreate.driverOnPdf')}</Notice>
                  <div className="form-grid form-grid--full">
                    {passengerFieldsForSection('transporte').map((field) => (
                      <FormField
                        key={field.column}
                        column={field.column}
                        full={field.multiline}
                        addressBook={transportBook(field.column)}
                      />
                    ))}
                    <TextInput
                      label={t('fields.externalRef.label')}
                      optional
                      help={t('fields.externalRef.help')}
                      error={externalRefMark}
                      {...form.register('externalRef')}
                    />
                  </div>
                </div>
              </FormSectionCard>
            </div>

            <aside className="form-layout__rail" aria-label={t('passengerCreate.railTitle')}>
              <div ref={validationCard} tabIndex={-1} className="form-layout__anchor">
                <Card title={t('passengerCreate.railTitle')}>
                  {/* Once the server has answered, it is the authority — the local
                      count is a hint about a form, and this is a verdict on a
                      document. Flat and with no penalty sentence, because the
                      sheet has no liable-party split to apportion one across. */}
                  {fieldErrors ? (
                    <PassengerValidationSummary
                      errors={fieldErrors}
                      okMessage={t('create.railReady')}
                    />
                  ) : (
                    <div className="stack-sm small">
                      {complete ? (
                        <Notice tone="success">{t('create.railReady')}</Notice>
                      ) : (
                        <Notice tone="warning">{t('create.railPending', { count: missing })}</Notice>
                      )}
                      <p className="small muted">{t('create.railHelp')}</p>
                    </div>
                  )}
                </Card>
              </div>

              <Card title={t('create.documentTitle')}>
                <div className="stack-sm small">
                  <div className="row-between">
                    <span className="muted">{t('list.columns.estado')}</span>
                    <EstadoChip estado="BORRADOR" />
                  </div>
                  <Datum
                    label={t('detail.type')}
                    value={
                      grouped
                        ? t('passengerCreate.typeGrouped', { count: services.fields.length })
                        : t('passengerCreate.typeSingle')
                    }
                  />
                  <Datum label={t('create.format')} value={t('create.formatValue')} />
                  <Datum
                    label={t('common.download')}
                    value={t('create.downloadValue', { count: downloadWindowDays(principal) })}
                  />

                  {/* #111 — one group, so a phone can pin it to the foot of the screen
                      (responsive/forms.css). On a desktop it is the same column of buttons. */}
                  <div className="stack-sm create-actions">
                    <MissingFieldsButton
                      count={missingCount}
                      refused={fieldErrors !== undefined}
                      target={validationCard}
                    />
                    <Button
                      variant="primary"
                      icon="check"
                      block
                      loading={busy}
                      disabled={busy || !complete}
                      onClick={() => void form.handleSubmit((raw) => submit(raw, true))()}
                    >
                      {busy
                        ? t('create.generating')
                        : grouped
                          ? t('passengerCreate.generateGrouped')
                          : t('passengerCreate.generate')}
                    </Button>
                    {complete ? null : (
                      <p className="small muted">{t('create.generateEnabledWhen')}</p>
                    )}
                    {/* #90 — NOT `form.handleSubmit`, for the same reason as the goods form:
                        the mandatory rules registered in `ui/form.tsx` must not stand between
                        this button and the network, because a draft is incomplete by
                        definition. `POST /passenger-decas` validates only when `generate` is
                        true. *Generar* keeps the rules and its own `!complete` gate. */}
                    <Button
                      block
                      onClick={() => void submit(form.getValues(), false)}
                      disabled={busy}
                    >
                      {t('common.saveDraft')}
                    </Button>
                  </div>
                </div>
              </Card>

              <Notice tone="info" title={t('create.deliverReminder')}>
                <span className="small">{t('detail.qrHelp')}</span>
              </Notice>
            </aside>
          </div>
        </form>
      </DecaFormProvider>
    </FormProvider>
  )
}

/**
 * Where a server error for this service field lands — always
 * `services.<servicioSeq>.<column>`, numbered whether or not the sheet is
 * grouped.
 *
 * 🚨 The number is **not** this block's `seq` prop, which is `undefined` while
 * the sheet has one service because it drives the heading and the BOE Sixth
 * override block. It is the number the *server* puts on the service:
 * `validatableFromPassengerInput` in `backend/src/http/routes/passenger-decas.ts`
 * stamps `seq: i + 1` over the `services` array exactly as sent, and every other
 * validating path reads the stored `seq`, which is written `i + 1` and read back
 * ordered. So a single-service sheet's service failures come back as
 * `servicioSeq: 1` — never null — and `passengerErrorsByField` stores them under
 * `services.1.<column>`.
 *
 * Keying them to the bare column instead is the defect this replaces: the
 * contract's prose described `servicioSeq` as set only for a *grouped* sheet
 * until 2026-09-11, and this function believed it. The rail still listed the
 * failure, so nothing looked broken, while the input the server named stayed
 * unmarked — in the default, single-service mode.
 *
 * `toInput` sends this block's row at exactly `index` in both modes (grouping
 * off slices to row 0), so `index + 1` is that number and there is no branch.
 *
 * The three override columns need no exception to it. They would need one if a
 * bare `conductor_nif` could address them, because it names the sheet's own
 * driver too and one key cannot address two inputs — but every key this function
 * makes is service-numbered and the sheet's own fields are looked up by the bare
 * column, so the two can never collide.
 */
function errorKeyFor(column: string, servicioSeq: number): string {
  return `services.${servicioSeq}.${column}`
}

/** Mandatory *now*, for the class chosen on this row — Art. 2(c) and 2(f). */
function serviceMandatory(column: string, clase: ServiceClassValue | ''): boolean {
  const def = passengerFieldDef(column)
  return def ? isMandatoryForClass(def, clase) : false
}

/**
 * One service — section 3's repeatable body.
 *
 * `seq` is the 1-based number when grouped and `undefined` when not, and it
 * drives the heading, the error keys and whether the BOE Sixth override block is
 * offered at all. Both branches run: single mode renders exactly one headerless
 * block with no overrides, grouping renders numbered ones that carry them. (The
 * goods form once had a `grouped` flag here whose header branch had never
 * executed; it was deleted rather than copied into this file.)
 *
 * 🚨 Two fields are rendered with explicit controls instead of through
 * `FormField`, and that is deliberate. `servicio_fecha` is a date with an
 * optional hour beside it (#102) and `servicio_clase` is an enum; `FieldDefLike` — the narrow overlap the goods and
 * passenger dictionaries share — cannot express either, and adding `control` to
 * `PassengerFieldDef` would make `'control' in def` in `form.tsx` narrow a
 * passenger def to the goods `FieldDef` with no compile error, since the match
 * there is purely structural. So the two controls live here and the dictionary
 * stays out of it.
 *
 * Neither is given `TextInput`/`SelectInput`'s `required` prop, which would
 * render an asterisk. The house rule is the other way round — "optional fields
 * are marked, mandatory ones are not" (`web/README.md`), because almost every
 * field on this form is mandatory — so `BaseField` marks the exceptions with
 * "(opcional)" and `GoodsCreateForm` passes `required` nowhere. The `required:`
 * rules inside `form.register` below are a different thing: react-hook-form
 * validation, which is what actually makes the two fields mandatory, and they
 * stay.
 */
function ServiceBlock({
  index,
  seq,
  serverErrors,
  onRemove,
}: {
  index: number
  seq: number | undefined
  serverErrors: Record<string, ApiFieldError>
  onRemove?: (() => void) | undefined
}) {
  const { t } = useTranslation()
  const form = useFormContext<PassengerFormValues>()

  const clase = form.watch(`services.${index}.servicio_clase`)
  const rowErrors = form.formState.errors.services?.[index]
  /**
   * The number the server will report this service's failures under — see
   * `errorKeyFor`. Derived from `index` rather than from `seq`, which is a
   * presentation flag and is `undefined` on a single-service sheet.
   */
  const servicioSeq = index + 1
  const messageFor = (column: keyof ServiceValues, client: string | undefined) =>
    serverErrors[errorKeyFor(column, servicioSeq)]?.message ?? client

  return (
    // `service-form`: a phone draws a line between one service and the next (#111). Not
    // `.service`, the detail screen's boxed style, which would change the desktop.
    <div className="stack-md service-form">
      {seq == null ? null : (
        <div className="row-between">
          <h3 className="small">{t('passengerCreate.service', { seq })}</h3>
          {onRemove ? (
            <IconButton icon="x" label={t('passengerCreate.removeService')} onClick={onRemove} />
          ) : null}
        </div>
      )}

      <div className="form-grid form-grid--full">
        <FormField
          column="servicio_origen"
          name={`services.${index}.servicio_origen`}
          errorKey={errorKeyFor('servicio_origen', servicioSeq)}
          full
          addressBook={{ kind: 'PLACE', column: `services.${index}.servicio_origen` }}
        />
        <FormField
          column="servicio_destino"
          name={`services.${index}.servicio_destino`}
          errorKey={errorKeyFor('servicio_destino', servicioSeq)}
          full
          addressBook={{ kind: 'PLACE', column: `services.${index}.servicio_destino` }}
        />

        <TextInput
          type="date"
          {...SERVICE_DATE_BOUNDS}
          label={t('fields.servicio_fecha.label')}
          error={messageFor('servicio_fecha', rowErrors?.servicio_fecha?.message)}
          {...form.register(`services.${index}.servicio_fecha`, {
            required: t('errors.required'),
          })}
        />
        {/* #102: the hour is optional — Art. 2.1(c) names the *fecha* and no hour. No `required`,
            so a blank one is a complete sheet, and the rail counts it that way. */}
        <TextInput
          type="time"
          label={t('fields.servicio_fecha.hora')}
          help={t('fields.servicio_fecha.horaHelp')}
          {...form.register(`services.${index}.servicio_hora`)}
        />

        <SelectInput
          label={t('fields.servicio_clase.label')}
          error={messageFor('servicio_clase', rowErrors?.servicio_clase?.message)}
          {...form.register(`services.${index}.servicio_clase`, {
            required: t('errors.required'),
          })}
        >
          <option value="">{t('common.none')}</option>
          {SERVICE_CLASSES.map((value) => (
            <option key={value} value={value}>
              {t(`serviceClass.${value}`)}
            </option>
          ))}
        </SelectInput>

        <FormField
          column="servicio_paradas"
          name={`services.${index}.servicio_paradas`}
          errorKey={errorKeyFor('servicio_paradas', servicioSeq)}
          mandatory={serviceMandatory('servicio_paradas', clase)}
          full
        />
        <FormField
          column="servicio_clase_ref"
          name={`services.${index}.servicio_clase_ref`}
          errorKey={errorKeyFor('servicio_clase_ref', servicioSeq)}
          mandatory={serviceMandatory('servicio_clase_ref', clase)}
          // Named by the class that requires it (Client, 2026-09-17). Only those
          // two classes get a narrower name: on `DISCRECIONAL` and `TURISTICO`
          // the field is still rendered, optional, and naming it after a service
          // that is not being reinforced would read worse than the column's own
          // heading — which is also the one the PDF prints for every class.
          {...(clase === 'REFUERZO' || clase === 'COLABORACION'
            ? { labelKey: `fields.servicio_clase_ref.label_${clase}` }
            : {})}
          full
        />
      </div>

      {/* The BOE Sixth overrides — a grouped sheet only. Optional here and
          mandatory on the sheet: the same three columns, asked twice, meaning
          "this service departs from the sheet" rather than "the sheet has no
          driver". That reading needs something to depart from, and a single
          service has nothing — it *is* the sheet. Asked twice there, the screen
          would offer six inputs for three concepts and let one route sheet name
          two different drivers, both of them printed
          (`backend/src/pdf/render-passenger.ts` puts the override in the
          service's detail lines and the sheet's own driver in section 4).
          Hiding them is the half of that guard the user can see; `toInput`'s
          `serviceOverride` is the half that survives a toggle. */}
      {seq == null ? null : (
        <>
          <p className="small muted">{t('passengerCreate.overridesHelp')}</p>
          <div className="form-grid form-grid--full">
            {OVERRIDE_COLUMNS.map((column) => (
              <FormField
                key={column}
                column={column}
                name={`services.${index}.${column}`}
                errorKey={errorKeyFor(column, servicioSeq)}
                mandatory={false}
                addressBook={transportBook(column, `services.${index}.`)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  )
}
